import { useCallback } from 'react';
import { Y200 } from '@atlaskit/theme/colors';
import type { SupportedThirdParty } from '../../../../common/constants/import-type';
import type { ThirdPartyEntity } from '../../../../common/types';
import { useContentForThirdParty } from '../../../../common/utils';
import {
	useThirdPartyState,
	useThirdPartySubfolders,
} from '../../../../controllers/import-state/selectors';

type Props = {
	thirdParty: SupportedThirdParty;
	shouldShowPlaceholderIcons: boolean;
};

export const useSubfolders = ({ thirdParty, shouldShowPlaceholderIcons }: Props) => {
	const { subfoldersSelectorLabel: label, subfoldersSelectorPlaceholder: placeholder } =
		useContentForThirdParty(thirdParty);

	const [{ selectedFolderId, selectedSubfolderId }] = useThirdPartyState();
	const [{ selectedSubfolders }, { setThirdPartyState }] = useThirdPartySubfolders();

	const onChange = useCallback(
		(selectedSubfolder: ThirdPartyEntity | null) => {
			setThirdPartyState({
				selectedSubfolderId: selectedSubfolder ? selectedSubfolder.id : null,
				selectedEntityId: null,
				selectedEntities: null,
				entities: null,
				entityStats: null,
				members: null,
				thirdPartyFieldConfigState: null,
				thirdPartyStatusMapping: null,
			});
		},
		[setThirdPartyState],
	);

	const onClear = useCallback(() => onChange(null), [onChange]);

	return {
		label,
		placeholder,
		starredOptionsLabel: '',
		otherOptionsLabel: '',
		noEntitiesFoundHeader: '',
		noEntitiesFoundDescription: '',
		errorOccurredHeader: '',
		entities: selectedSubfolders,
		selectedEntityId: selectedSubfolderId,
		isLoading: false,
		isDisabled: selectedFolderId === null,
		isClearable: true,
		hasErrorOccurred: false,
		onChange,
		onClear,
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onRetry: () => {},
		placeholderColor: shouldShowPlaceholderIcons ? Y200 : undefined,
		isEmptyEntities: !selectedSubfolders || selectedSubfolders.length === 0,
	};
};
